
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { login as reduxLogin } from "../redux/actions/authActions";
import LogoImg from "../images/GMGC.png";
import { 
  FiUser, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiLogIn, 
  FiCalendar, 
  FiClock, 
  FiBook, 
  FiCheckCircle, 
  FiShield,
  FiSun, 
  FiMoon,
  FiExternalLink  // Add this import for external link icon
} from "react-icons/fi";
import { MdSchool } from "react-icons/md";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get loading state from Redux
  const { loading, error } = useSelector(state => state.auth);

  /* ------------------ Dark Mode ------------------ */
  useEffect(() => {
    const saved = localStorage.getItem("darkMode");
    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

    if (saved === "true" || (!saved && systemDark)) {
      setDarkMode(true);
      document.documentElement.classList.add("dark");
    }
  }, []);

  const toggleDarkMode = () => {
    const value = !darkMode;
    setDarkMode(value);
    localStorage.setItem("darkMode", value);
    document.documentElement.classList.toggle("dark", value);
  };

  /* ------------------ Login Submit ------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      const res = await dispatch(reduxLogin(email, password));

      if (res?.meta?.status) {
        const user = res.data;
        
        // Store individual fields
        localStorage.setItem("userId", user.id);
        localStorage.setItem("user_name", user.user_name);
        localStorage.setItem("full_name", user.full_name);
        localStorage.setItem("email", user.email);
        localStorage.setItem("phone", user.contact_no);
        localStorage.setItem("type_id", user.type_id);

        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
        }

        toast.success("Login successful");
        navigate("/dashboard");
      } else {
        toast.error(res?.meta?.message || "Invalid credentials");
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error(err.response?.data?.meta?.message || "Login failed. Please try again.");
    }
  };

  // Show error from Redux state
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 dark:from-gray-900 dark:via-purple-900 dark:to-pink-900 flex items-center justify-center p-4 transition-colors duration-300">
      {/* Dark Mode Toggle */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-6 right-6 p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg z-10 hover:shadow-xl transition-shadow duration-300"
        aria-label="Toggle dark mode"
      >
        {darkMode ? <FiSun className="text-yellow-400 w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
      </button>

      <div className="w-full max-w-6xl flex flex-col lg:flex-row rounded-3xl overflow-hidden shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        {/* Left College Info Section - Purple Theme */}
        <div className="lg:w-1/2 bg-gradient-to-br from-purple-700 via-violet-700 to-pink-700 p-12 text-white flex flex-col justify-center relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600 rounded-full -translate-y-32 translate-x-16 opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-600 rounded-full translate-y-24 -translate-x-12 opacity-20"></div>
          
          {/* Logo and College Info */}
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-8">
              <MdSchool className="w-16 h-16 mr-4 text-yellow-300" />
              <img src={LogoImg} alt="GMGC Logo" className="w-24 h-24 object-contain" />
            </div>
            
            <div className="text-center mb-8">
              <div className="text-5xl font-bold mb-2 font-serif tracking-wide">
                GOKHALE MEMORIAL
              </div>
              <div className="text-4xl font-bold mb-2 font-serif tracking-wide">
                GIRLS' COLLEGE
              </div>
              <div className="text-2xl font-semibold text-yellow-300 mb-1">
                ESTD 1938
              </div>
              <div className="text-lg text-purple-200 font-medium">
                Kolkata, West Bengal
              </div>
            </div>

            <div className="h-1 w-32 bg-yellow-400 mx-auto mb-8 rounded-full"></div>

            {/* Features for Attendance System */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-yellow-300 mb-4 text-center">
                Smart Attendance System
              </h3>
              
              {[
                { icon: FiCalendar, text: "Digital Attendance Tracking", color: "text-green-300" },
                { icon: FiClock, text: "Real-time Monitoring", color: "text-blue-300" },
                { icon: FiBook, text: "Academic Management", color: "text-pink-300" },
                { icon: FiShield, text: "Secure & Reliable", color: "text-yellow-300" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300">
                  <f.icon className={`w-6 h-6 ${f.color}`} />
                  <span className="font-medium">{f.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-center gap-3 text-sm text-purple-200">
              <FiCheckCircle className="w-5 h-5 text-green-300" /> 
              Empowering education since 1938
            </div>
          </div>
        </div>

        {/* Right Login Form */}
        <div className="lg:w-1/2 p-10 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Welcome Header */}
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white font-serif mb-3">
                Faculty Portal
              </h2>
              <div className="h-1 w-24 bg-purple-600 mx-auto mb-4 rounded-full"></div>
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                Access the Attendance Management System
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="relative group">
                <FiUser className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400"
                  placeholder="Faculty Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
                <div className="absolute inset-0 rounded-xl border-2 border-transparent group-focus-within:border-purple-300 pointer-events-none"></div>
              </div>

              {/* Password Input */}
              <div className="relative group">
                <FiLock className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition-all duration-300 group-hover:border-purple-400"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-300"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
                <div className="absolute inset-0 rounded-xl border-2 border-transparent group-focus-within:border-purple-300 pointer-events-none"></div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex justify-between items-center">
                <label className="flex items-center gap-3 text-gray-700 dark:text-gray-300 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="sr-only"
                      disabled={loading}
                    />
                    <div className={`w-5 h-5 border-2 rounded ${rememberMe ? 'bg-purple-600 border-purple-600' : 'border-gray-400 group-hover:border-purple-500'} transition-all duration-300 flex items-center justify-center`}>
                      {rememberMe && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium">Remember me</span>
                </label>
                {/* <a 
                  href="#" 
                  className="text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors duration-300 hover:underline"
                >
                  Forgot password?
                </a> */}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-pink-700 transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
              >
                {loading ? (
                  <span className="flex items-center gap-3">
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing in...
                  </span>
                ) : (
                  <>
                    <FiLogIn className="w-5 h-5" />
                    <span>Sign In to Dashboard</span>
                  </>
                )}
              </button>

              {/* College Motto */}
              <div className="text-center pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-3">
                  "Where Tradition Meets Technology in Education"
                </p>
                
                {/* Updated Footer with EBLUESYS Link */}
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    © {new Date().getFullYear()} All Rights Reserved.
                  </p>
                  <a 
                    href="https://www.ebluesys.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors duration-300 group"
                  >
                    <span>Developed by EBLUESYS</span>
                    <FiExternalLink className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </a>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;